import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import moment from 'moment';
import { uploadFile } from 'api/borderos';
import {
  baixarArquivoImportado,
  getListaArquivosProcessados,
  getListaRegistrosArquivosProcessados,
  processarAtualizacaoArquivos,
  uploadArquivoCnab
} from 'api/atualizacaoTitulo';
import sanitizeFileName from 'utils/sanitizeFileName';
import { limparDocumento } from 'utils/documentoAlfanumerico';
import { atualizacaoTituloFiltroInicial } from '../validation/atualizacaoTitulo.filters';

const unwrap = res => {
  if (res && res.data && res.data.original) return res.data.original;
  if (res && res.data) return res.data;
  return {};
};

const toDateFromYMD = value => {
  if (!value) return null;
  const d = new Date(String(value) + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? null : d;
};

const toNumber = value => {
  const n = Number(String(value || '').replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
};

const mapArquivoApiToUi = row => ({
  uuid: row.UUID,
  dataProcessamento: row.DATAPROCESSAMENTO,
  nomeArquivo: row.NOMEARQUIVO,
  qtdRegistros: row.QTDREGISTROS,
  usuarioInclusao: row.NOME != null ? String(row.NOME) : '',
  possuiNaoIntegrados: String(row.POSSUINAOINTEGRADOS || 'N') === 'S',
  s3path: row.S3PATH,
  __downloading: false
});

const mapRegistroApiToUi = row => ({
  uuid: row.UUID,
  arquivoUuid: row.CNABATUALIZACAOIMPORTADOSUUID,
  cedente: row.CEDENTE,
  sacadoNome: row.SACADONOME,
  cpfCnpjSacado: row.CPFCNPJSACADO,
  numeroDocumento: row.NUMERODOCUMENTO,
  vencimento: toDateFromYMD(row.VENCIMENTO),
  valor: toNumber(row.VALOR),
  nossoNumero: row.NOSSONUMERO,
  statusIntegracao: row.CODTITULO ? 'Encontrado' : 'Não encontrado',
  statusIntegracaoOk: !!row.CODTITULO
});

const parseDDMMYYYY = value => {
  if (!value) return null;
  const m = moment(value, 'DD/MM/YYYY', true);
  return m.isValid() ? m : null;
};

const inDateRange = (dateObj, ini, fim) => {
  if (!dateObj) return false;
  const m = moment(dateObj);
  if (ini && m.isBefore(ini, 'day')) return false;
  if (fim && m.isAfter(fim, 'day')) return false;
  return true;
};

const filterArquivos = (arquivos, filtros) => {
  const f = filtros || {};
  const dtIni = parseDDMMYYYY(f.data_inicio);
  const dtFim = parseDDMMYYYY(f.data_final);

  if (!dtIni && !dtFim) return arquivos || [];

  return (arquivos || []).filter(row => {
    const dataProc = row.dataProcessamento || row.data_processamento || row.DATAPROCESSAMENTO;
    return inDateRange(dataProc, dtIni, dtFim);
  });
};

const filterRegistros = (registros, filtros) => {
  const f = filtros || {};

  const vencIni = parseDDMMYYYY(f.vencimento_inicio);
  const vencFim = parseDDMMYYYY(f.vencimento_final);

  const valorMin =
    f.valor_min !== '' && f.valor_min != null ? Number(String(f.valor_min).replace(',', '.')) : null;
  const valorMax =
    f.valor_max !== '' && f.valor_max != null ? Number(String(f.valor_max).replace(',', '.')) : null;

  const cpfSacado = limparDocumento(f.cpfcnpj_sacado || '');
  const nomeSacado = String(f.nome_sacado || '')
    .trim()
    .toLowerCase();
  const ndoc = String(f.ndoc || '')
    .trim()
    .toLowerCase();

  return (registros || []).filter(row => {
    if (cpfSacado) {
      const rowCpf = limparDocumento(row.cpfCnpjSacado || row.CPFCNPJSACADO || '');
      if (!rowCpf.includes(cpfSacado)) return false;
    }

    if (nomeSacado) {
      const rowNome = String(row.sacadoNome || row.SACADONOME || '').toLowerCase();
      if (!rowNome.includes(nomeSacado)) return false;
    }

    if (vencIni || vencFim) {
      const venc = row.vencimento || row.VENCIMENTO;
      if (!inDateRange(venc, vencIni, vencFim)) return false;
    }

    const rowValor = Number(row.valor != null ? row.valor : row.VALOR);
    if (valorMin != null && Number.isFinite(rowValor) && rowValor < valorMin) return false;
    if (valorMax != null && Number.isFinite(rowValor) && rowValor > valorMax) return false;

    if (ndoc) {
      const rowDoc = String(row.numeroDocumento || row.NUMERODOCUMENTO || '').toLowerCase();
      if (!rowDoc.includes(ndoc)) return false;
    }

    return true;
  });
};

const useAtualizacaoTitulo = ({ clienteAtual, tipoOperacaoCode }) => {
  const [filtrosAplicados, setFiltrosAplicados] = useState(atualizacaoTituloFiltroInicial);

  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadingUuid, setDownloadingUuid] = useState(null);

  const [viewMode, setViewMode] = useState('ARQUIVOS');

  const [arquivos, setArquivos] = useState([]);
  const [isLoadingArquivos, setIsLoadingArquivos] = useState(false);

  const [registros, setRegistros] = useState([]);
  const [isLoadingRegistros, setIsLoadingRegistros] = useState(false);
  const [registrosByArquivo, setRegistrosByArquivo] = useState({});

  const downloadingRef = useRef(null);

  useEffect(() => {
    downloadingRef.current = downloadingUuid;
  }, [downloadingUuid]);

  const cpfcnpjSan = useMemo(() => {
    const cnpj = clienteAtual && clienteAtual.cnpj ? String(clienteAtual.cnpj) : '';
    return limparDocumento(cnpj);
  }, [clienteAtual && clienteAtual.cnpj ? String(clienteAtual.cnpj) : '']);

  const listarDocumentosProcessados = useCallback(
    async filtros => {
      if (!cpfcnpjSan) return;

      const f = filtros ? filtros : filtrosAplicados;

      setIsLoadingArquivos(true);
      try {
        const res = await getListaArquivosProcessados(cpfcnpjSan, f);
        const payload = unwrap(res);
        const lista = Array.isArray(payload.data) ? payload.data : [];
        setArquivos(lista.map(mapArquivoApiToUi));
      } finally {
        setIsLoadingArquivos(false);
      }
    },
    [cpfcnpjSan, filtrosAplicados]
  );

  const didAutoLoadRef = useRef(false);
  useEffect(() => {
    if (!cpfcnpjSan) return;
    if (didAutoLoadRef.current) return;
    didAutoLoadRef.current = true;
    listarDocumentosProcessados(atualizacaoTituloFiltroInicial);
  }, [cpfcnpjSan, listarDocumentosProcessados]);

  const aplicarFiltros = useCallback(
    async values => {
      const v = values ? values : atualizacaoTituloFiltroInicial;
      setFiltrosAplicados(v);
      await listarDocumentosProcessados(v);
    },
    [listarDocumentosProcessados]
  );

  const limparFiltros = useCallback(async () => {
    setFiltrosAplicados(atualizacaoTituloFiltroInicial);
    await listarDocumentosProcessados(atualizacaoTituloFiltroInicial);
  }, [listarDocumentosProcessados]);

  const deleteFile = useCallback(file => {
    setFiles(prev => prev.filter(fl => fl.id !== file.id));
  }, []);

  const retryUpload = useCallback(
    file => {
      setIsUploading(true);

      uploadFile(
        file.data,
        progressValue => {
          file.progress = progressValue;
          file.statusCode = 0;
          setFiles(prev => [...prev]);
        },
        clienteAtual ? clienteAtual.clienteEspecial : false,
        tipoOperacaoCode,
        clienteAtual ? clienteAtual.clienteOperaConnect : false,
        false,
        clienteAtual ? clienteAtual.liberaXmlRedesconto : false,
        clienteAtual ? clienteAtual.redescontoAvulso : false
      )
        .then(res => {
          file.response = res.data;
          file.statusCode = res.status;
        })
        .catch(err => {
          file.response = err.response;
          file.statusCode = (err.response && err.response.status) || 400;
        })
        .finally(() => {
          setFiles(prev => [...prev]);
          setIsUploading(false);
        });
    },
    [clienteAtual, tipoOperacaoCode]
  );

  const filesAcceptedHandler = useCallback(
    acceptedFiles => {
      setIsUploading(true);

      const parsedFiles = acceptedFiles.map(acceptedFile => {
        const sanitizedName = sanitizeFileName(acceptedFile.name);
        const sanitizedFile = new File([acceptedFile], sanitizedName, {
          type: acceptedFile.type,
          lastModified: acceptedFile.lastModified
        });
        const data = new FormData();
        data.append('file', sanitizedFile);

        return {
          id: Math.random()
            .toString(36)
            .substr(2, 9),
          data: data,
          file: sanitizedFile,
          name: sanitizedName,
          statusCode: 0,
          response: null,
          progress: 0
        };
      });

      setFiles(prev => [...prev, ...parsedFiles]);

      parsedFiles.forEach(parsedFile => {
        uploadFile(
          parsedFile.data,
          progressValue => {
            parsedFile.progress = progressValue;
            parsedFile.statusCode = 0;
            setFiles(prev => [...prev]);
          },
          clienteAtual ? clienteAtual.clienteEspecial : false,
          tipoOperacaoCode,
          clienteAtual ? clienteAtual.clienteOperaConnect : false,
          false,
          clienteAtual ? clienteAtual.liberaXmlRedesconto : false,
          clienteAtual ? clienteAtual.redescontoAvulso : false
        )
          .then(res => {
            parsedFile.response = res.data;
            parsedFile.statusCode = res.status;
            setFiles(prev => [...prev]);
          })
          .catch(err => {
            parsedFile.response = err.response;
            parsedFile.statusCode = (err.response && err.response.status) || 500;
            setFiles(prev => [...prev]);
          })
          .finally(() => {
            setIsUploading(false);
          });
      });
    },
    [clienteAtual, tipoOperacaoCode]
  );

  const processarArquivos = useCallback(async () => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const dadosParaProcessamento = [];

      for (const fileItem of files) {
        const formData = new FormData();
        formData.append('arquivo', fileItem.file);

        const cnpj = clienteAtual && clienteAtual.cnpj ? clienteAtual.cnpj : '';
        formData.append('cpf_cnpj', cnpj);

        const uploadResponse = await uploadArquivoCnab(formData);
        const uploadData = uploadResponse && uploadResponse.data ? uploadResponse.data : {};
        const s3path = uploadData.s3path;
        const nomearquivo = uploadData.nomearquivo;

        const responseData = fileItem && fileItem.response ? fileItem.response : {};
        const titulos = responseData.titulos ? responseData.titulos : [];

        dadosParaProcessamento.push({
          cpf_cnpj: cnpj,
          arquivo: { s3path: s3path, nomearquivo: nomearquivo },
          infoArquivo: { id: fileItem.id, nomeOriginal: fileItem.name },
          titulos: titulos.map(titulo => ({
            id: titulo.id,
            linha: titulo.linha,
            sacado: titulo.sacado,
            duplicata: titulo.duplicata,
            processamentoLote: titulo.processamentoLote
          }))
        });
      }

      await processarAtualizacaoArquivos(dadosParaProcessamento);
      await listarDocumentosProcessados(filtrosAplicados);
      setFiles([]);
    } finally {
      setIsUploading(false);
    }
  }, [files, clienteAtual, listarDocumentosProcessados, filtrosAplicados]);

  const verRegistros = useCallback(
    async arquivo => {
      if (!arquivo || !arquivo.uuid) return;

      setViewMode('REGISTROS');

      const cached = registrosByArquivo[arquivo.uuid];
      if (cached && cached.length > 0) {
        setRegistros(cached);
        return;
      }

      setIsLoadingRegistros(true);
      try {
        const res = await getListaRegistrosArquivosProcessados(arquivo.uuid, {
          cpfcnpj: cpfcnpjSan
        });

        const payload = unwrap(res);
        const lista = Array.isArray(payload.data) ? payload.data : [];
        const mapped = lista.map(mapRegistroApiToUi);

        setRegistros(mapped);
        setRegistrosByArquivo(prev => {
          const next = { ...prev };
          next[arquivo.uuid] = mapped;
          return next;
        });
      } finally {
        setIsLoadingRegistros(false);
      }
    },
    [cpfcnpjSan, registrosByArquivo]
  );

  const voltarArquivos = useCallback(() => {
    setRegistros([]);
    setViewMode('ARQUIVOS');
  }, []);

  const baixarArquivoImportadoS3 = useCallback(async (s3path, nomearquivo, uuid) => {
    if (!s3path || !uuid) return;

    setDownloadingUuid(uuid);
    setArquivos(prev =>
      (prev || []).map(r =>
        r.uuid === uuid ? { ...r, __downloading: true } : { ...r, __downloading: false }
      )
    );

    try {
      const response = await baixarArquivoImportado(s3path, nomearquivo);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomearquivo);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingUuid(null);
      setArquivos(prev => (prev || []).map(r => (r.uuid === uuid ? { ...r, __downloading: false } : r)));
    }
  }, []);

  const gridData = useMemo(() => {
    if (viewMode === 'ARQUIVOS') return filterArquivos(arquivos, filtrosAplicados);
    return filterRegistros(registros, filtrosAplicados);
  }, [viewMode, arquivos, registros, filtrosAplicados]);

  const isLoading = viewMode === 'ARQUIVOS' ? isLoadingArquivos : isLoadingRegistros;

  return {
    files,
    isUploading,
    deleteFile,
    retryUpload,
    filesAcceptedHandler,
    processarArquivos,

    viewMode,
    gridData,
    isLoading,

    verRegistros,
    voltarArquivos,
    baixarArquivoImportadoS3,

    filtrosAplicados,
    aplicarFiltros,
    limparFiltros,
    listarDocumentosProcessados,
    downloadingUuid,
    downloadingRef
  };
};

export default useAtualizacaoTitulo;
